import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import permissionsService from "../../services/permissionsService";

const EditUserModal = ({ isOpen, onClose, user, onSubmit, tenants = [], isPlatformAdmin = false }) => {
  // Hierarquia do mais baixo ao mais alto.
  const JOB_ROLES = ["Estagiário", "Assistente", "Analista", "Coordenador", "Gerente", "Diretor", "C-Level"];
  const [profileOptions, setProfileOptions] = useState(["Administrador", "Analista", "Usuario"]);

  useEffect(() => {
    if (!isOpen) return;
    permissionsService
      .getProfiles()
      .then((rows) => {
        const names = (Array.isArray(rows) ? rows : []).map((p) => p.name).filter(Boolean);
        if (names.length) setProfileOptions(names);
      })
      .catch(() => {});
  }, [isOpen]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    profile: "Administrador",
    job_role: "",
    is_assignable: false,
    status: "Ativo",
    password: "",
    tenant_id: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        username: user.username || "",
        profile: user.profile || "Administrador",
        job_role: user.job_role || "",
        is_assignable: !!user.is_assignable,
        status: user.is_active ? "Ativo" : "Inativo",
        password: "",
        tenant_id: user.tenant_id || "",
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // monta o payload sem senha
    const payload = {
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      username: form.username,
      profile: form.profile,
      job_role: form.job_role || null,
      is_assignable: !!form.is_assignable,
      is_active: form.status === "Ativo",
    };

    if (isPlatformAdmin) {
      payload.tenant_id = form.tenant_id ? Number(form.tenant_id) : null;
    }

    // só envia a senha se o usuário preencheu
    if (form.password && form.password.trim() !== "") {
      payload.password = form.password;
    }

    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-xl bg-white p-4 sm:p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Editar Usuário</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar">
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Nome</label>
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600">
                Sobrenome
              </label>
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600">
              Usuário (login)
            </label>
            <input
              name="username"
              value={form.username}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600">Perfil</label>
            <select
              name="profile"
              value={form.profile}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {/* Garante que o perfil atual apareça mesmo se não vier na lista. */}
              {(profileOptions.includes(form.profile) ? profileOptions : [form.profile, ...profileOptions]).map((p) => (
                <option key={p} value={p}>{p === "Usuario" ? "Usuário" : p}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Perfil de permissão (definido em Permissões).</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600">Função (cargo no time)</label>
            <select
              name="job_role"
              value={form.job_role}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— Não definido —</option>
              {JOB_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Classificação p/ relatórios (separada da permissão).</p>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={!!form.is_assignable}
                onChange={(e) => setForm((prev) => ({ ...prev, is_assignable: e.target.checked }))}
                className="h-4 w-4 accent-blue-600"
              />
              Aparece na lista de <strong>Responsável / Fila</strong>
            </label>
          </div>

          {isPlatformAdmin && (
            <div>
              <label className="text-xs font-medium text-gray-600">Empresa</label>
              <select
                name="tenant_id"
                value={form.tenant_id}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Sem empresa</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600">
              Nova senha (opcional)
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Deixe em branco para manter a atual"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              Salvar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
